/**
 * Context Registry for Admin Contextual Panel
 *
 * Provides a plugin architecture where context-specific panels can register
 * themselves. The ContextualPanel doesn't need to know about specific contexts -
 * each context module registers its own URL patterns and components.
 *
 * Usage:
 *   import { registerContext } from './contextRegistry';
 *   import UserContextPanel from './contexts/UserContextPanel';
 *
 *   registerContext({
 *     name: 'user',
 *     patterns: [
 *       { pattern: /\/(profiles|pr|media)\/([^/?]+)/, extract: (match) => ({ slug: match[2] }) }
 *     ],
 *     component: UserContextPanel
 *   });
 */

import qlog from "views_shared/app/javascript/scripts/qlog";

const registeredContexts = [];

/**
 * Register a context plugin
 *
 * @param {Object} config - Context configuration
 * @param {string} config.name - Unique identifier for this context
 * @param {Array} config.patterns - URL patterns that trigger this context
 * @param {Function} config.patterns[].pattern - RegExp to match against URL
 * @param {Function} config.patterns[].extract - Function to extract props from match
 * @param {React.Component} config.component - React component to render for this context
 * @param {number} [config.priority=0] - Higher priority contexts are matched first
 */
export function registerContext(config) {
  const { name, patterns, component, priority = 0 } = config;

  if (!name || !patterns || !component) {
    qlog.error("[ContextRegistry] Invalid context config:", config);
    return;
  }

  // Remove existing registration with same name (allows hot reload)
  const existingIndex = registeredContexts.findIndex((c) => c.name === name);
  if (existingIndex >= 0) {
    qlog.debug("[ContextRegistry] Replacing existing context:", name);
    registeredContexts.splice(existingIndex, 1);
  }

  registeredContexts.push({ name, patterns, component, priority });

  // Keep sorted by priority (highest first)
  registeredContexts.sort((a, b) => b.priority - a.priority);

  qlog.trace("[ContextRegistry] Registered context:", name, "priority:", priority);
}

/**
 * Detect context from URL by checking registered patterns
 *
 * @param {string} url - URL path to check
 * @returns {Object|null} - Detected context with type, props, and component
 */
export function detectContext(url) {
  qlog.trace("[ContextRegistry] Detecting context for URL:", url);

  // Use reduce to find the first matching context/pattern combination
  const result = registeredContexts.reduce((found, context) => {
    if (found) return found;

    // Check each pattern in this context
    return context.patterns.reduce((patternMatch, { pattern, extract }) => {
      if (patternMatch) return patternMatch;

      const match = url.match(pattern);
      if (match) {
        qlog.debug("[ContextRegistry] Matched context:", context.name, "pattern:", pattern);
        return {
          type: context.name,
          props: extract ? extract(match) : {},
          component: context.component,
        };
      }
      return null;
    }, null);
  }, null);

  if (!result) {
    qlog.trace("[ContextRegistry] No context matched for URL:", url);
  }

  return result;
}

/**
 * Get all registered contexts (useful for debugging)
 */
export function getRegisteredContexts() {
  return [...registeredContexts];
}

/**
 * Clear all registered contexts (useful for testing)
 */
export function clearRegistry() {
  registeredContexts.length = 0;
}
